// module imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// file imports
import axiosInstance from '@/lib/axios';

const getFeedbackQuestions = createAsyncThunk('feedbackQuestions/getFeedbackQuestions', async (slug: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`/feedback-questions/${slug}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Retrieving feedback questions failed');
  }
});

const addFeedbackQuestion = createAsyncThunk('feedbackQuestions/addFeedbackQuestion', async ({ slug, question }: { slug: string; question: any }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post(`/feedback-questions/${slug}`, { question });
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Adding feedback question failed');
  }
});

const updateFeedbackQuestion = createAsyncThunk('feedbackQuestions/updateFeedbackQuestion', async ({ slug, questionId, questionData }: { slug: string; questionId: string; questionData: any }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.patch(`/feedback-questions/${slug}?questionId=${questionId}`, questionData);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Updating feedback question failed');
  }
});

const removeFeedbackQuestion = createAsyncThunk('feedbackQuestions/removeFeedbackQuestion', async ({ slug, questionId }: { slug: string; questionId: string }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.delete(`/feedback-questions/${slug}`, { data: { questionId } });
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Removing feedback question failed');
  }
});

const updateQuestionOrder = createAsyncThunk('feedbackQuestions/updateQuestionOrder', async ({ slug, orderedQuestionIds }: { slug: string; orderedQuestionIds: string[] }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.patch(`/feedback-questions/${slug}`, { orderedQuestionIds });
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Updating question order failed');
  }
});

const feedbackQuestionSlice = createSlice({
  name: 'feedbackQuestions',
  initialState: {
    data: null as any,
    loading: false,
    error: null as string | null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // getFeedbackQuestions
      .addCase(getFeedbackQuestions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFeedbackQuestions.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getFeedbackQuestions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // updateFeedbackQuestion
      .addCase(updateFeedbackQuestion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateFeedbackQuestion.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateFeedbackQuestion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // addFeedbackQuestion
      .addCase(addFeedbackQuestion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addFeedbackQuestion.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(addFeedbackQuestion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // removeFeedbackQuestion
      .addCase(removeFeedbackQuestion.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeFeedbackQuestion.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(removeFeedbackQuestion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // updateQuestionOrder - No loading state for optimistic updates
      .addCase(updateQuestionOrder.pending, (state) => {
        state.error = null;
      })
      .addCase(updateQuestionOrder.fulfilled, () => {})
      .addCase(updateQuestionOrder.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export { addFeedbackQuestion, getFeedbackQuestions, removeFeedbackQuestion, updateFeedbackQuestion, updateQuestionOrder };
export default feedbackQuestionSlice.reducer;
